import axios from "axios";

// -----------------------------------------------------------------------------
// API base resolution
// -----------------------------------------------------------------------------
// The Emergent Kubernetes ingress routes `/api/*` to the backend service on
// EVERY host the frontend is served from — the preview URL
// (https://publish-validator.preview.emergentagent.com), the Emergent-assigned
// production host (https://socialgem-mvp.emergent.host), and the custom
// production domain (https://postmoro.com, https://www.postmoro.com).
//
// Historically we used build-time REACT_APP_BACKEND_URL. That broke on the
// custom domain because the production build baked
// REACT_APP_BACKEND_URL=https://socialgem-mvp.emergent.host into the bundle,
// so the frontend on postmoro.com made cross-origin fetches to .emergent.host
// — a host that is not reachable from the browser on postmoro.com
// (DNS / Cloudflare / CORS). Result: every API call (including signup)
// returned "Failed to fetch".
//
// Fix: in the browser, ALWAYS call the same origin the page is served from.
// This is correct because the ingress already routes /api/* to the backend
// regardless of host. We keep the env var as a fallback only for non-browser
// contexts (SSR/build tooling) which never actually hit this path at runtime.
// -----------------------------------------------------------------------------
const ENV_BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function resolveBackendUrl() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return ENV_BACKEND;
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// -----------------------------------------------------------------------------
// Session-token handling — Phase A2 security remediation (F-03 + F-09).
//
// Previously the frontend read a session token out of localStorage and
// attached it as an ``Authorization: Bearer`` header on every request.
// That defeated the HttpOnly cookie protection because any successful
// XSS could exfiltrate the token from localStorage.
//
// The backend now sets a Secure + HttpOnly + SameSite=Lax cookie on
// login/signup/session responses. The browser attaches that cookie
// automatically to every same-origin /api/* request thanks to
// ``withCredentials: true`` above. No JS-readable session credential
// exists any more.
//
// One-time cleanup: legacy browsers that already have the historical
// ``sg_session_token`` value cached in localStorage should have it
// removed on app boot. We never read the value into memory or send
// it anywhere — this is a fire-and-forget removal.
// -----------------------------------------------------------------------------
try {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem("sg_session_token");
  }
} catch (_err) {
  /* localStorage disabled (private mode, etc.) — nothing to clean up */
}

// -----------------------------------------------------------------------------
// Phase 3 — Active business workspace header.
// Reads a workspace id from localStorage (set by WorkspaceSelector) and
// attaches it to every /api/* request as X-Business-Workspace-Id. The
// backend authorizes strictly — an invalid header → 403 workspace_forbidden
// and we clear the stored id so the app falls back to the primary.
// -----------------------------------------------------------------------------
export const ACTIVE_WORKSPACE_KEY = "postmoro_active_business_workspace_id";

export function getActiveWorkspaceId() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(ACTIVE_WORKSPACE_KEY) || null;
  } catch (_e) {
    return null;
  }
}

export function setActiveWorkspaceId(id) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (id) window.localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
    else window.localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    // Broadcast to hooks/components subscribed via window "storage" listener.
    window.dispatchEvent(new CustomEvent("postmoro:workspace-changed", {
      detail: { workspaceId: id || null },
    }));
  } catch (_e) {
    /* ignore */
  }
}

api.interceptors.request.use((config) => {
  const wsId = getActiveWorkspaceId();
  if (wsId) {
    config.headers = config.headers || {};
    // Do NOT override an explicit header a caller already set.
    if (!config.headers["X-Business-Workspace-Id"]) {
      config.headers["X-Business-Workspace-Id"] = wsId;
    }
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    // 403 workspace_forbidden → the selected workspace is no longer
    // authorized (archived / plan downgrade / stale id). Clear + reload.
    try {
      const detail = error?.response?.data?.detail || {};
      const code = typeof detail === "object" ? detail.code : null;
      if (error?.response?.status === 403 &&
          (code === "workspace_forbidden" ||
           code === "workspace_entitlement_locked")) {
        const wasActive = getActiveWorkspaceId();
        setActiveWorkspaceId(null);
        if (wasActive && typeof window !== "undefined") {
          // Delay reload slightly so a toast (if any) can render.
          setTimeout(() => { window.location.reload(); }, 100);
        }
      }
    } catch (_e) {
      /* ignore */
    }
    return Promise.reject(error);
  },
);

// -----------------------------------------------------------------------------
// F-04 (Phase B1a) — Global 429 (rate limit) response handling.
//
// Backend returns HTTP 429 for auth abuse (login, signup, forgot-password IP,
// reset-password) and AI cost-burst violations. We surface a single, friendly
// toast per burst so users get a clear "slow down" cue without a wall of
// duplicate notifications. Never auto-retry — that would either mask the
// abuse or make the client complicit in the throttled behavior.
//
// Suppression window: 3 seconds. Multiple 429s inside the window collapse
// into ONE toast. This handles the common case where a page fires several
// parallel calls that all trip the same limit.
//
// The interceptor also RE-REJECTS the error so callers still see the 429
// (their local .catch handlers can decide to hide their own inline error
// UI if desired — the global toast is the primary signal).
// -----------------------------------------------------------------------------
let _lastRateLimitToastAt = 0;
const _RATE_LIMIT_TOAST_COOLDOWN_MS = 3000;

// -----------------------------------------------------------------------------
// Content Safety Phase S1 — 422 content_policy_block / 503 content_safety_unavailable.
//
// Backend returns:
//   * HTTP 422 with detail.code === "content_policy_block" when generated /
//     uploaded / to-be-published content violates Postmoro policy.
//   * HTTP 503 with detail.code === "content_safety_unavailable" when the
//     required moderation/safety service could not complete.
//
// Both cases surface a single generic user-friendly toast (never provider
// name, category, score, or classifier reasoning). Callers may still
// implement their own inline UX by inspecting the error — the helpers
// below expose stable predicates for that.
// -----------------------------------------------------------------------------
export const CONTENT_POLICY_BLOCK_CODE = "content_policy_block";
export const CONTENT_SAFETY_UNAVAILABLE_CODE = "content_safety_unavailable";

export function isContentPolicyBlockError(err) {
  try {
    const status = err && err.response && err.response.status;
    const data = err && err.response && err.response.data;
    if (status !== 422 || !data) return false;
    const detail = data.detail;
    if (detail && typeof detail === "object" && detail.code === CONTENT_POLICY_BLOCK_CODE) return true;
    // Post-normalization form: interceptor stashed the code in _safety.
    if (data._safety && data._safety.code === CONTENT_POLICY_BLOCK_CODE) return true;
    return false;
  } catch (_e) {
    return false;
  }
}

export function isContentSafetyUnavailableError(err) {
  try {
    const status = err && err.response && err.response.status;
    const data = err && err.response && err.response.data;
    if (status !== 503 || !data) return false;
    const detail = data.detail;
    if (detail && typeof detail === "object" && detail.code === CONTENT_SAFETY_UNAVAILABLE_CODE) return true;
    if (data._safety && data._safety.code === CONTENT_SAFETY_UNAVAILABLE_CODE) return true;
    return false;
  } catch (_e) {
    return false;
  }
}

export function contentSafetyErrorMessage(err) {
  try {
    const detail = err && err.response && err.response.data && err.response.data.detail;
    if (detail && typeof detail === "object" && detail.message) return String(detail.message);
  } catch (_e) {
    /* fall through */
  }
  if (isContentPolicyBlockError(err)) {
    return "This content can't be generated or published because it may violate Postmoro's content safety rules. Please revise and try again.";
  }
  if (isContentSafetyUnavailableError(err)) {
    return "We couldn't complete the required content safety check right now. Nothing was published. Please try again shortly.";
  }
  return null;
}

// -----------------------------------------------------------------------------
// Safe error-message extraction (React #31 hardening)
//
// Any API error may arrive as:
//   * a string
//   * a FastAPI HTTPException detail object ({code, message, ...})
//   * a Pydantic 422 validation array ([{loc, msg, type, ...}])
//   * null / undefined
//   * something else entirely
//
// Passing any non-string value directly into a React child (toast, banner,
// error text) throws "Objects are not valid as a React child" — the
// minified variant is React error #31, which crashes Generate Post if
// a raw detail object ever hits toast.error(). This helper GUARANTEES a
// plain string return so callers never render an object.
//
// It never exposes stack traces, internal exception classes, or field
// values from the request. Only user-facing prose is returned.
// -----------------------------------------------------------------------------
export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  try {
    if (!err) return fallback;
    // Prefer a specific safety message when applicable — those are already
    // strings.
    const safety = contentSafetyErrorMessage(err);
    if (safety) return String(safety);

    const data = err && err.response && err.response.data;
    if (!data) {
      const msg = err && err.message;
      return typeof msg === "string" && msg ? msg : fallback;
    }

    const detail = data.detail;
    if (typeof detail === "string" && detail.trim()) return detail;

    // Structured object detail — HTTPException(detail={code, message, ...})
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      if (typeof detail.message === "string" && detail.message.trim()) {
        return detail.message;
      }
      if (typeof detail.error === "string" && detail.error.trim()) {
        return detail.error;
      }
      if (typeof detail.code === "string" && detail.code.trim()) {
        // Fallback: humanize the code slug.
        return detail.code.replace(/[_-]+/g, " ").trim();
      }
    }

    // Pydantic validation array — flatten to short prose.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === "string") return first;
      if (first && typeof first === "object") {
        const msg = typeof first.msg === "string" ? first.msg : "";
        const loc = Array.isArray(first.loc)
          ? first.loc.filter((p) => p !== "body" && p !== "query" && p !== "path").join(".")
          : "";
        if (loc && msg) return `${loc}: ${msg}`;
        if (msg) return msg;
      }
    }

    // Some endpoints return { message } at the top level instead of detail.
    if (typeof data.message === "string" && data.message.trim()) return data.message;

    return fallback;
  } catch (_e) {
    return fallback;
  }
}

let _lastSafetyToastAt = 0;
const _SAFETY_TOAST_COOLDOWN_MS = 3000;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error && error.response && error.response.status;
      if (status === 429) {
        const now = Date.now();
        if (now - _lastRateLimitToastAt > _RATE_LIMIT_TOAST_COOLDOWN_MS) {
          _lastRateLimitToastAt = now;
          // Lazy-import so this file does not force sonner into any
          // pre-auth bundle chunk that doesn't already reference it.
          import("sonner")
            .then(({ toast }) => {
              toast.error("Too many requests. Please wait a moment and try again.", {
                id: "rate-limit-429",
                duration: 4000,
              });
            })
            .catch(() => {
              /* toast library unavailable — swallow silently */
            });
        }
      }
      // Content safety — generic, provider-agnostic messaging.
      if (isContentPolicyBlockError(error) || isContentSafetyUnavailableError(error)) {
        // Preserve the machine-readable code for programmatic callers,
        // then rewrite ``detail`` to the human message so legacy callers
        // that render ``err.response.data.detail`` directly (as a string)
        // see the correct generic UX text instead of "[object Object]".
        try {
          const original = error.response.data.detail;
          const humanMessage = (original && typeof original === "object" && original.message)
            ? String(original.message)
            : contentSafetyErrorMessage(error);
          error.response.data._safety = {
            code: (original && typeof original === "object" && original.code) || null,
            reason_code: (original && typeof original === "object" && original.reason_code) || null,
            message: humanMessage,
          };
          if (humanMessage) {
            error.response.data.detail = humanMessage;
          }
        } catch (_e) {
          /* leave error as-is if normalization fails */
        }
        const now = Date.now();
        if (now - _lastSafetyToastAt > _SAFETY_TOAST_COOLDOWN_MS) {
          _lastSafetyToastAt = now;
          const msg = contentSafetyErrorMessage(error);
          const toastId = isContentPolicyBlockError(error)
            ? "content-policy-block"
            : "content-safety-unavailable";
          if (msg) {
            import("sonner")
              .then(({ toast }) => {
                toast.error(msg, { id: toastId, duration: 6000 });
              })
              .catch(() => {
                /* toast library unavailable — swallow silently */
              });
          }
        }
      }
    } catch (_err) {
      /* interceptor must never throw */
    }
    return Promise.reject(error);
  },
);

export default api;
